import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { useTranslation } from "react-i18next";
import config from "../config";
import BuzzShell from "./BuzzShell";

function ZoneSelect({ zones, value, onChange, t, allowEmpty = true }) {
  return (
    <select className="bz-select" value={value ?? ""}
      onChange={e => onChange(e.target.value ? Number(e.target.value) : null)}>
      {allowEmpty && <option value="">— {t("No zone")} —</option>}
      {zones.map(z => <option key={z.id} value={z.id}>{z.name}</option>)}
    </select>
  );
}

export default function BuzzTables() {
  const { t } = useTranslation();
  const [tables, setTables] = useState(null);
  const [zones, setZones] = useState([]);
  const [bulk, setBulk] = useState({ count: 10, prefix: "", zoneId: null, startAt: 1 });
  const [single, setSingle] = useState({ label: "", zoneId: null });
  const [qrModal, setQrModal] = useState(null);
  const [editModal, setEditModal] = useState(null); // {id, label, zoneId}
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    const [{ data: tb }, { data: zs }] = await Promise.all([
      axios.get(`${config.API_BASE_URL}/buzz/tables`),
      axios.get(`${config.API_BASE_URL}/buzz/zones`),
    ]);
    setTables(tb);
    setZones(zs);
  }, []);
  useEffect(() => { load(); }, [load]);

  const createBulk = async () => {
    setBusy(true);
    try {
      await axios.post(`${config.API_BASE_URL}/buzz/tables/bulk`, {
        count: Number(bulk.count), prefix: bulk.prefix || undefined,
        zoneId: bulk.zoneId, startAt: Number(bulk.startAt) || 1,
      });
      load();
    } finally { setBusy(false); }
  };

  const createSingle = async () => {
    if (!single.label.trim()) return;
    await axios.post(`${config.API_BASE_URL}/buzz/tables`, {
      label: single.label.trim(), zoneId: single.zoneId,
    });
    setSingle({ label: "", zoneId: single.zoneId });
    load();
  };

  const patch = async (id, body) => {
    await axios.patch(`${config.API_BASE_URL}/buzz/tables/${id}`, body);
    load();
  };

  const rotate = async (id) => {
    if (!window.confirm(t("Rotate QR? The old printed QR will stop working."))) return;
    await axios.post(`${config.API_BASE_URL}/buzz/tables/${id}/rotate-token`);
    load();
  };

  const remove = async (id) => {
    try {
      await axios.delete(`${config.API_BASE_URL}/buzz/tables/${id}`);
      load();
    } catch (e) {
      if (e?.response?.status === 409) {
        alert(t("This table has call history — deactivate it instead."));
      }
    }
  };

  const pdfUrl = `${config.API_BASE_URL}/buzz/tables/qr.pdf`;

  if (tables === null) return <BuzzShell><div className="bz-empty">…</div></BuzzShell>;

  // Onboarding: καθόλου τραπέζια ακόμα → wizard (πρώτα ζώνες, μετά τραπέζια)
  if (tables.length === 0) {
    return (
      <BuzzShell>
        <div className="bz-card">
          <h3>👋 {t("Let's set up your venue")}</h3>
          <ol className="bz-steps">
            <li>{t("Create your zones (optional — e.g. Hall, Garden).")}</li>
            <li>{t("Tell us how many tables you have — we create them instantly.")}</li>
            <li>{t("Download the QR PDF and print it.")}</li>
            <li>{t("Scan one with your phone and watch the call arrive on the Board.")}</li>
          </ol>
        </div>
        <div className="bz-card">
          <p className="bz-hint">
            💡 {t("Want zones? Create them first in the")}{" "}
            <a href="/buzz/zones" style={{ color: "#d97706", fontWeight: 600 }}>{t("Zones")}</a>{" "}
            {t("tab — then come back here.")}
          </p>
        </div>
        <div className="bz-card">
          <h4>{t("Create tables")}</h4>
          <div className="bz-form-row">
            <label>{t("How many tables?")}</label>
            <input type="number" min="1" max="200" value={bulk.count}
              onChange={e => setBulk({ ...bulk, count: e.target.value })} />
          </div>
          <div className="bz-form-row">
            <label>{t("Label prefix (optional)")}</label>
            <input type="text" placeholder={t("e.g. A")} value={bulk.prefix}
              onChange={e => setBulk({ ...bulk, prefix: e.target.value })} />
          </div>
          <div className="bz-form-row">
            <label>{t("Zone")}</label>
            <ZoneSelect zones={zones} value={bulk.zoneId} t={t}
              onChange={zoneId => setBulk({ ...bulk, zoneId })} />
          </div>
          <button className="bz-btn primary" disabled={busy} onClick={createBulk}>
            {t("Create tables")}
          </button>
          <p className="bz-hint" style={{ marginTop: 8 }}>
            {t("Tip: run this once per zone — e.g. 10 tables in Hall, then 14 more in Garden.")}
          </p>
        </div>
      </BuzzShell>
    );
  }

  return (
    <BuzzShell>
      <div className="bz-toolbar">
        <a href={pdfUrl} target="_blank" rel="noopener noreferrer">
          <button className="bz-btn primary">🖨 {t("Download all QR (PDF)")}</button>
        </a>
        <span style={{ flex: 1 }} />
        <input type="text" className="bz-input" placeholder={t("New table label")} value={single.label}
          style={{ minWidth: 140, flex: "0 1 160px" }}
          onChange={e => setSingle({ ...single, label: e.target.value })}
          onKeyDown={e => e.key === "Enter" && createSingle()} />
        <ZoneSelect zones={zones} value={single.zoneId} t={t}
          onChange={zoneId => setSingle({ ...single, zoneId })} />
        <button className="bz-btn" onClick={createSingle}>➕ {t("Add")}</button>
      </div>

      <div className="bz-tablewrap">
      <table className="bz-table-list">
        <thead>
          <tr>
            <th>{t("Table")}</th><th>{t("Zone")}</th><th>QR</th><th>{t("Active")}</th><th></th>
          </tr>
        </thead>
        <tbody>
          {tables.map(tb => (
            <tr key={tb.id} className={tb.active ? "" : "bz-inactive"}>
              <td><b>{tb.label}</b></td>
              <td>
                <ZoneSelect zones={zones} value={tb.zone_id} t={t}
                  onChange={zoneId => patch(tb.id, { zoneId })} />
              </td>
              <td>
                <button className="bz-btn ghost" onClick={() => setQrModal(tb)}>QR</button>{" "}
                <a href={`${config.API_BASE_URL}/buzz/tables/${tb.id}/qr.pdf`} target="_blank" rel="noopener noreferrer">PDF</a>
              </td>
              <td>
                <input type="checkbox" checked={!!tb.active}
                  onChange={() => patch(tb.id, { active: !tb.active })} />
              </td>
              <td style={{ whiteSpace: "nowrap" }}>
                <button className="bz-btn ghost" title={t("Edit")}
                  onClick={() => setEditModal({ id: tb.id, label: tb.label, zoneId: tb.zone_id })}>✏️</button>{" "}
                <button className="bz-btn ghost" title={t("Rotate QR")} onClick={() => rotate(tb.id)}>↻</button>{" "}
                <button className="bz-btn ghost" title={t("Delete")} onClick={() => remove(tb.id)}>🗑</button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      </div>

      {editModal && (
        <div className="bz-modal-bg" onClick={() => setEditModal(null)}>
          <div className="bz-modal" onClick={(e) => e.stopPropagation()} style={{ textAlign: "left" }}>
            <h3>{t("Edit table")}</h3>
            <div className="bz-form-row">
              <label>{t("Table")}</label>
              <input type="text" maxLength={30} value={editModal.label} autoFocus
                onChange={e => setEditModal({ ...editModal, label: e.target.value })} />
            </div>
            <div className="bz-form-row">
              <label>{t("Zone")}</label>
              <ZoneSelect zones={zones} value={editModal.zoneId} t={t}
                onChange={zoneId => setEditModal({ ...editModal, zoneId })} />
            </div>
            <p className="bz-hint">{t("The QR does not change when you rename — no need to reprint.")}</p>
            <div className="bz-toolbar" style={{ marginTop: 10 }}>
              <button className="bz-btn primary" disabled={!editModal.label.trim()} onClick={async () => {
                await patch(editModal.id, { label: editModal.label.trim(), zoneId: editModal.zoneId });
                setEditModal(null);
              }}>{t("Save")}</button>
              <button className="bz-btn" onClick={() => setEditModal(null)}>{t("Close")}</button>
            </div>
          </div>
        </div>
      )}

      {qrModal && (
        <div className="bz-modal-bg" onClick={() => setQrModal(null)}>
          <div className="bz-modal" onClick={(e) => e.stopPropagation()}>
            <h3>{t("Table")} {qrModal.label}</h3>
            <img alt="QR" src={`${config.API_BASE_URL}/buzz/tables/${qrModal.id}/qr.png?ts=${Date.now()}`} />
            <div className="bz-qr-url">{qrModal.publicUrl}</div>
            <button className="bz-btn" style={{ marginTop: 12 }} onClick={() => setQrModal(null)}>{t("Close")}</button>
          </div>
        </div>
      )}
    </BuzzShell>
  );
}
